import moment from 'moment';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import { ROUTES_API } from '@/services/Constants';
import { processRequest } from '@/services/Api';
import * as activityActions from '../store/actions';
import { activityActionTypes } from '../store/constants';
import { isEmptyReportActivity } from '../helpers';
import { getReportActivity } from '../store/selectors';

import type { SagaIterator } from 'redux-saga';

export default function* (): Generator {
	yield all([
		takeLatest(activityActionTypes.GET_REPORT_ACTIVITY_REQUEST, handleReportActivityRequest),
		takeLatest(
			activityActionTypes.GET_PREVIOUS_REPORT_ACTIVITY_REQUEST,
			handlePreviousReportActivityRequest,
		),
		takeLatest(
			activityActionTypes.GET_NEXT_REPORT_ACTIVITY_REQUEST,
			handleNextReportActivityRequest,
		),
		takeLatest(activityActionTypes.GET_PLAYED_DAYS_REQUEST, handlePlayedDaysRequest),
	]);
}

export function* handleReportActivityRequest({ payload }: any): SagaIterator {
	const { date, weeks, callback } = payload || {};

	try {
		let activityData;

		const { data: reportData1 } = yield call(
			processRequest,
			ROUTES_API.ACTIVITY_REPORT,
			'GET',
			{
				date,
				weeks,
				sort_direction: 'desc',
			},
		);
		activityData = reportData1;

		if (isEmptyReportActivity(activityData.activity) && !activityData.started_date) {
			const weeksCount = 12;
			const { data: reportData2 } = yield call(
				processRequest,
				ROUTES_API.ACTIVITY_REPORT,
				'GET',
				{
					date,
					weeks: weeksCount,
					sort_direction: 'desc',
				},
			);
			yield put(
				activityActions.setPreviousDate(
					moment(date).subtract(weeksCount, 'weeks').format('YYYY-MM-DD'),
				),
			);
			activityData = reportData2;
		}

		yield put(
			activityActions.getReportActivitySuccess(
				activityData.activity,
				activityData.started_date,
			),
		);

		callback && callback();
	} catch (e) {
		yield put(activityActions.getReportActivityError(e));
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handlePreviousReportActivityRequest({ payload }: any): SagaIterator {
	const { date } = payload || {};

	try {
		yield put(activityActions.setPreviousDate(date));

		const { data } = yield call(processRequest, ROUTES_API.ACTIVITY_REPORT, 'GET', {
			date,
			sort_direction: 'desc',
		});

		const activity = yield select(getReportActivity);

		yield put(
			activityActions.getReportActivitySuccess(
				{ ...activity, ...data.activity },
				data.started_date,
			),
		);
	} catch (e) {
		yield put(activityActions.getReportActivityError(e));
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleNextReportActivityRequest({ payload }: any): SagaIterator {
	const { date, callback } = payload || {};

	try {
		yield put(activityActions.setNextDate(date));

		const { data } = yield call(processRequest, ROUTES_API.ACTIVITY_REPORT, 'GET', {
			date,
			sort_direction: 'desc',
		});

		const activity = yield select(getReportActivity);

		yield put(
			activityActions.getReportActivitySuccess(
				{ ...data.activity, ...activity },
				data.started_date,
			),
		);

		callback && callback();
	} catch (e) {
		yield put(activityActions.getReportActivityError(e));
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handlePlayedDaysRequest({ payload }: any): SagaIterator {
	const { date } = payload || {};

	try {
		const { data } = yield call(
			processRequest,
			ROUTES_API.PLAYED_DAYS,
			'GET',
			date ? { date } : undefined,
		);
		yield put(activityActions.getPlayedDaysSuccess(data));
	} catch (e) {
		yield put(activityActions.getPlayedDaysError(e));
		yield put(errorHandlerActions.handleError(e));
	}
}
